import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  const menuItems = [
    { name: 'About Us', href: '/' },
    { name: 'Our Services', href: '/services' },
    { name: 'Drive with us', href: '/drive-with-us' },
    { name: 'Blog', href: '/blog' },
    { name: 'Safety', href: '/safety' },
    { name: 'Advertisement', href: '/advertise' },
    { name: 'Contact us', href: '/contact' },
  ];

  // Detect user's OS for app store link
  const getAppStoreLink = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;

    // iOS devices -> App Store
    if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
      return 'https://apps.apple.com/az/app/nura-mobility/id6749004862';
    }

    // Android devices -> Play Store
    if (/android/i.test(userAgent)) {
      return 'https://play.google.com/store/apps/details?id=in.co.nuraemobility.nuraApplication';
    }

    // Mac desktop -> App Store
    if (/Macintosh|MacIntel|MacPPC|Mac68K/.test(userAgent)) {
      return 'https://apps.apple.com/az/app/nura-mobility/id6749004862';
    }

    // Windows (and everything else) -> Play Store
    return 'https://play.google.com/store/apps/details?id=in.co.nuraemobility.nuraApplication';
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const isActivePath = (path) => {
    return location.pathname === path;
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-black shadow-lg' : 'bg-black shadow-md'
    }`}>
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo - Left Side */}
          <div className="flex items-center flex-shrink-0">
            <Link to="/">
              <img 
                src="https://customer-assets.emergentagent.com/job_nurae-electric/artifacts/1q3pi61z_N%20white%20logo.png" 
                alt="NURA" 
                className="h-12 w-auto"
              />
            </Link>
          </div>

          {/* Desktop Menu and Download Button - Right Side */}
          <div className="hidden lg:flex items-center space-x-4 flex-shrink-0">
            <div className="flex items-center space-x-1 bg-black rounded-full px-3 py-2 border border-gray-800">
              {menuItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`relative px-4 py-2 font-medium transition-all duration-300 rounded-full whitespace-nowrap ${
                    isActivePath(item.href)
                      ? 'bg-[#bceb39] text-black' 
                      : 'text-white hover:text-[#bceb39]'
                  }`}
                  style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif", fontSize: '16px' }}
                >
                  <span>
                    {item.name}
                  </span>
                </Link>
              ))}
            </div>
            
            {/* Download Now Button - OS Detection */}
            <a
              href={getAppStoreLink()}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#bceb39] text-black font-bold px-6 py-3 rounded-full hover:bg-[#a8d632] transition-colors whitespace-nowrap"
            >
              Download Now
            </a>
          </div>

          {/* Mobile Download Button */}
          <div className="flex lg:hidden items-center space-x-2 flex-shrink-0">
            <a
              href={getAppStoreLink()}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#bceb39] text-black font-bold px-4 py-2 rounded-full text-sm whitespace-nowrap"
            >
              Download
            </a>
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden ml-2">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="text-gray-300 hover:text-[#bceb39] transition-colors"
            >
              {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-black border-t border-gray-800">
          <div className="px-4 py-4 space-y-1">
            {menuItems.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`block px-4 py-3 rounded-lg font-medium transition-colors ${
                  isActivePath(item.href)
                    ? 'bg-[#bceb39] text-black' 
                    : 'text-gray-300 hover:bg-gray-900'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {item.name}
              </Link>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
